"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  ItinerarySuccess,
  StopPin,
  VenueOption,
} from "@/lib/planner/api-types";
import { isClosed } from "@/lib/planner/api-types";
import type { ItineraryLeg } from "@/lib/planner/itinerary";
import type { PlannerPreferences } from "@/lib/planner/preferences";
import {
  stopCategories,
  stopDurationMinutes,
  PRICE_CEILING,
  type StopTimeKey,
  type StopTimeOverrides,
} from "@/lib/planner/stops";
import { assessedVenueOpening, placeEvidenceLabel, venueVisitWarnings } from "@/lib/planner/venue-policy";
import { priceTierLabel } from "@/lib/planner/places-display";
import { trackEvent } from "@/lib/analytics";
import {
  plannerEventForEdit,
  trackPlannerEvent,
} from "@/lib/planner/analytics-events";
import { scoreOutOf100Label } from "@/lib/conditions/score-display";
import { difficultyColour, difficultyLabel, difficultySymbol, ASPECT_LABEL } from "./trail-colours";

type Props = {
  slug: string;
  plan: ItinerarySuccess;
  preferences: PlannerPreferences;
  touchedFields: readonly string[];
  onApply: (next: ItinerarySuccess) => void;
  onApplyAlternative: (patch: Partial<PlannerPreferences>) => void;
};

type StopType = "coffee" | "lunch" | "apres";
type MoveLeg = Extract<ItineraryLeg, { kind: "lift" | "trail" | "link" | "bus" }>;
const isMove = (l: ItineraryLeg): l is MoveLeg => l.kind !== "stop";

const STOP_ICON: Record<StopType, string> = { coffee: "☕", lunch: "🍽️", apres: "🍻" };

// Sensible retime bounds per stop slot (decimal hours), so a nudged stop stays
// in its part of the day. insertStops still drops any stop that cannot fit.
const STOP_BOUNDS: Record<StopTimeKey, { lo: number; hi: number }> = {
  lunch: { lo: 11, hi: 15 },
  coffeeAm: { lo: 8.5, hi: 12.5 },
  coffeePm: { lo: 13, hi: 16.5 },
  apres: { lo: 12, hi: 18 },
};

function pad(n: number): string {
  return String(n).padStart(2, "0");
}
function hhmm(decimalHour: number): string {
  // Carry minutes that round up to 60 into the hour (e.g. 10.997 -> 11:00).
  const total = Math.round(decimalHour * 60);
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}
function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** Which retime override slot a stop row maps to (coffee splits am/pm at 13:00). */
function stopKeyFor(row: StopRow): StopTimeKey {
  if (row.stopType === "lunch") return "lunch";
  if (row.stopType === "apres") return "apres";
  return row.time < 13 ? "coffeeAm" : "coffeePm";
}

function venueStopLabel(stopType: StopType, name: string | null): string {
  const verb = stopType === "coffee" ? "Coffee at" : stopType === "apres" ? "Apres at" : "Lunch at";
  return `${verb} ${name ?? "a mountain spot"}`;
}

/** Rebuild the map stop pins from edited legs (mirrors compose). */
function pinsFromLegs(legs: ItineraryLeg[]): StopPin[] {
  const pins: StopPin[] = [];
  for (const leg of legs) {
    if (leg.kind !== "stop") continue;
    const lat = leg.place?.lat;
    const lng = leg.place?.lng;
    if (lat == null || lng == null) continue;
    pins.push({
      stopType: leg.stopType,
      label: leg.label,
      placeId: leg.placeId,
      lat,
      lng,
      name: leg.place?.name ?? null,
      mapsUri: leg.place?.mapsUri ?? null,
      departHour: leg.departHour,
    });
  }
  return pins;
}

function routeAlternativeEditorMeta(alternative: ItinerarySuccess["routeAlternatives"][number]): string {
  return `${alternative.totals.skiKm.toFixed(1)} km · ${Math.round(
    alternative.totals.verticalM,
  ).toLocaleString("en-GB")} m vertical`;
}

type DescentRow = {
  type: "descent";
  legIndex: number;
  time: number;
  name: string;
  score: number | null;
  aspect: string | null;
};
type StopRow = {
  type: "stop";
  legIndex: number;
  stopType: StopType;
  time: number;
  label: string;
  nodeId: string;
  placeId: string | null;
};
type Row = DescentRow | StopRow;

function touchedWith(
  touchedFields: readonly string[],
  extra: readonly string[] = [],
): string[] {
  const out: string[] = [];
  for (const path of [...touchedFields, ...extra]) {
    if (!out.includes(path)) out.push(path);
    if (out.length >= 40) break;
  }
  return out;
}

export function PlanEditor({ slug, plan, preferences, touchedFields, onApply, onApplyAlternative }: Props) {
  // Stop edits accumulate in a local preferences copy; changing the panel
  // preferences (which regenerates the plan) resets them.
  const [localPrefs, setLocalPrefs] = useState<PlannerPreferences>(preferences);
  // Per-stop retime overrides, accumulated as the user nudges stop times.
  const [stopTimeOverrides, setStopTimeOverrides] = useState<StopTimeOverrides>({});
  useEffect(() => {
    setLocalPrefs(preferences);
    setStopTimeOverrides({});
  }, [preferences]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [swapOpen, setSwapOpen] = useState<number | null>(null);
  const [venueOpen, setVenueOpen] = useState<number | null>(null);

  // Sport drives the difficulty labels/symbols in the swap list so they match
  // the timeline (MTB shows IMBA grades + symbols, ski shows piste colours).
  const sport = preferences.sport;

  const weekday = useMemo(() => {
    if (!plan.scoredDate) return new Date().getUTCDay();
    return new Date(`${plan.scoredDate}T12:00:00Z`).getUTCDay();
  }, [plan.scoredDate]);

  const rows = useMemo<Row[]>(() => buildRows(plan), [plan]);

  const postEdit = useCallback(
    async (
      payload: {
        preferences: PlannerPreferences;
        lockedLegs: ItineraryLeg[];
        forcedNextEdgeId?: string;
        stopTimeOverrides?: StopTimeOverrides;
        touchedFields?: readonly string[];
      },
      kind: string,
    ) => {
      setBusy(true);
      setError(null);
      try {
        const res = await fetch(`/api/planner/${encodeURIComponent(slug)}/edit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // Carry the sport discriminant so the edit route coerces the
          // preferences into the right shape (MTB edits would otherwise be
          // coerced back to ski). Read from the preferences being sent.
          body: JSON.stringify({
            ...payload,
            touchedFields: payload.touchedFields ?? touchedFields,
            editContext: plan.editContext,
            sport: payload.preferences.sport,
          }),
        });
        if (!res.ok) throw new Error("edit failed");
        const resp = await res.json();
        if (isClosed(resp)) {
          setError("The planner has closed for the day.");
          return;
        }
        const next = resp as ItinerarySuccess;
        if (!next.itinerary?.legs?.length) {
          setError("We could not apply that change. Reset and plan your day again.");
          return;
        }
        onApply(next);
        trackEvent("planner_edited", { slug, kind });
        const plannerEvent = plannerEventForEdit(kind);
        if (plannerEvent) {
          trackPlannerEvent(plannerEvent, {
            slug,
            sport: payload.preferences.sport,
            dayStyle: payload.preferences.dayStyle,
            action: kind,
          });
        }
      } catch {
        setError("We could not apply that change. Please try again.");
      } finally {
        setBusy(false);
      }
    },
    [slug, onApply, plan.editContext, touchedFields],
  );

  // Plain functions (not memoised) so they always read the live override state.
  const swapRun = (legIndex: number, edgeId: string) => {
    setSwapOpen(null);
    const lockedLegs = plan.itinerary.legs.slice(0, legIndex).filter(isMove);
    postEdit({
      preferences: localPrefs,
      lockedLegs,
      forcedNextEdgeId: edgeId,
      stopTimeOverrides,
    }, "swap_run");
  };

  // Keep the runs, re-stop with the given preferences + retime overrides.
  const restop = (
    nextPrefs: PlannerPreferences,
    overrides: StopTimeOverrides,
    kind: string,
    extraTouched: readonly string[] = [],
  ) => {
    setLocalPrefs(nextPrefs);
    setStopTimeOverrides(overrides);
    const lockedLegs = plan.itinerary.legs.filter(isMove);
    postEdit({
      preferences: nextPrefs,
      lockedLegs,
      stopTimeOverrides: overrides,
      touchedFields: touchedWith(touchedFields, extraTouched),
    }, kind);
  };

  const changeVenue = useCallback(
    (legIndex: number, opt: VenueOption) => {
      const previous = plan.itinerary.legs[legIndex];
      if (previous?.kind !== "stop" || opt.source !== "openstreetmap") return;
      const opening = assessedVenueOpening(opt, weekday, previous.departHour + (opt.accessMinutes ?? 0) / 120, previous.arriveHour - (opt.accessMinutes ?? 0) / 120);
      if (opening === "closed" || (opt.accessMinutes ?? 0) !== (previous.place?.accessMinutes ?? 0)) return;
      setVenueOpen(null);
      setError(null);
      const legs = plan.itinerary.legs.map((l, i) => {
        if (i !== legIndex || l.kind !== "stop") return l;
        return {
          ...l,
          placeId: opt.placeId,
          label: venueStopLabel(l.stopType, opt.name),
          place: {
            name: opt.name,
            category: opt.category,
            priceLevel: opt.priceLevel,
            rating: opt.rating,
            cuisine: opt.cuisine,
            mapsUri: opt.mapsUri,
            reservable: opt.reservable,
            lat: opt.lat,
            lng: opt.lng,
            source: opt.source, sourceUrl: opt.sourceUrl, sourceCheckedAt: opt.sourceCheckedAt,
            sourceUpdatedAt: opt.sourceUpdatedAt, accessDistanceM: opt.accessDistanceM, accessMinutes: opt.accessMinutes,
            openingState: assessedVenueOpening(opt, weekday, l.departHour + (opt.accessMinutes ?? 0) / 120, l.arriveHour - (opt.accessMinutes ?? 0) / 120),
          },
        };
      });
      const oldWarnings = new Set(previous.place ? ["open", "unknown"].flatMap((state) => venueVisitWarnings(previous.place!, state as "open" | "unknown", previous.stopType)) : []);
      const warnings = [...plan.itinerary.warnings.filter((warning) => !oldWarnings.has(warning)), ...venueVisitWarnings(opt, opening, previous.stopType)];
      const walkingKm = Math.max(0, (plan.itinerary.totals.walkingKm ?? 0) + ((opt.accessDistanceM ?? 0) - (previous.place?.accessDistanceM ?? 0)) * 2 / 1000);
      const renameVenue = (text: string) => previous.place?.name ? text.split(previous.place.name).join(opt.name ?? "the planned stop") : text;
      const explanation = plan.routeExplanation;
      onApply({ ...plan, itinerary: { ...plan.itinerary, legs, warnings, totals: { ...plan.itinerary.totals, walkingKm } }, stops: pinsFromLegs(legs), routeExplanation: { ...explanation, summary: renameVenue(explanation.summary), highlights: explanation.highlights.map(renameVenue), tradeoffs: explanation.tradeoffs.map(renameVenue), caveats: explanation.caveats.map(renameVenue), skippedSectors: explanation.skippedSectors.map(renameVenue) } });
      trackEvent("planner_edited", { slug, kind: "change_venue" });
      trackPlannerEvent("planner_stop_edited", {
        slug,
        sport: preferences.sport,
        dayStyle: preferences.dayStyle,
        action: "change_venue",
      });
    },
    [plan, onApply, preferences.dayStyle, preferences.sport, slug, weekday],
  );

  const venuesFor = useCallback(
    (stop: StopRow): VenueOption[] => {
      const cats = stopCategories(stop.stopType, localPrefs);
      if (cats.length === 0) return [];
      const ceiling = stop.stopType === "lunch" ? PRICE_CEILING[localPrefs.lunch.price] : 4;
      const duration = stopDurationMinutes(stop.stopType, localPrefs.lunch.style) / 60;
      const selectedLeg = plan.itinerary.legs[stop.legIndex];
      const currentAccess = selectedLeg.kind === "stop" ? selectedLeg.place?.accessMinutes ?? 0 : 0;
      return (plan.venueOptionsByNode[stop.nodeId] ?? []).filter(
        (v) =>
          v.source === "openstreetmap" &&
          cats.includes(v.category) &&
          (v.priceLevel == null || v.priceLevel <= ceiling) &&
          // A local venue swap cannot silently add walking or shift lift times.
          (v.accessMinutes ?? 0) === currentAccess &&
          assessedVenueOpening(v, weekday, stop.time + (v.accessMinutes ?? 0) / 120, stop.time + (v.accessMinutes ?? 0) / 120 + duration) !== "closed",
      );
    },
    [plan, localPrefs, weekday],
  );

  const removeStop = (stopType: StopType, time: number) => {
    let next = localPrefs;
    let touchedPath = "";
    if (stopType === "lunch") {
      next = { ...localPrefs, lunch: { ...localPrefs.lunch, style: "skip" } };
      touchedPath = "lunch.style";
    } else if (stopType === "apres") {
      next = { ...localPrefs, apres: "skip" };
      touchedPath = "apres";
    } else {
      const cs = localPrefs.coffeeStops;
      const morning = time < 13;
      const ncs = morning
        ? cs === "both" ? "afternoon" : "skip"
        : cs === "both" ? "morning" : "skip";
      next = { ...localPrefs, coffeeStops: ncs };
      touchedPath = "coffeeStops";
    }
    restop(next, stopTimeOverrides, "remove_stop", [touchedPath]);
  };

  // Nudge any stop (lunch, morning/afternoon coffee, apres) earlier or later.
  // The override accumulates so repeated nudges compound off the chosen target,
  // not the placed time, and the server places the venue within +/-30 min of it.
  const retimeStop = (row: StopRow, deltaMin: number) => {
    const key = stopKeyFor(row);
    const bounds = STOP_BOUNDS[key];
    const base = stopTimeOverrides[key] ?? row.time;
    const next = clamp(base + deltaMin / 60, bounds.lo, bounds.hi);
    restop(localPrefs, { ...stopTimeOverrides, [key]: next }, "retime_stop");
  };

  const tryRouteAlternative = (alternative: ItinerarySuccess["routeAlternatives"][number]) => {
    setError(null);
    onApplyAlternative(alternative.preferencePatch);
    trackEvent("planner_edited", { slug, kind: "route_alternative", alternative: alternative.id });
  };

  // Which stops can still be added (not already present).
  const hasLunch = localPrefs.lunch.style !== "skip";
  const hasCoffeeAm = localPrefs.coffeeStops === "morning" || localPrefs.coffeeStops === "both";
  const hasCoffeePm = localPrefs.coffeeStops === "afternoon" || localPrefs.coffeeStops === "both";
  const hasApres = localPrefs.apres !== "skip";

  const addStops: { label: string; apply: () => void }[] = [];
  if (!hasLunch)
    addStops.push({
      label: "+ Lunch",
      apply: () =>
        restop(
          { ...localPrefs, lunch: { ...localPrefs.lunch, style: "cheap" } },
          stopTimeOverrides,
          "add_stop",
          ["lunch.style"],
        ),
    });
  if (!hasCoffeeAm)
    addStops.push({
      label: "+ Morning coffee",
      apply: () =>
        restop(
          { ...localPrefs, coffeeStops: localPrefs.coffeeStops === "afternoon" ? "both" : "morning" },
          stopTimeOverrides,
          "add_stop",
          ["coffeeStops"],
        ),
    });
  if (!hasCoffeePm)
    addStops.push({
      label: "+ Afternoon coffee",
      apply: () =>
        restop(
          { ...localPrefs, coffeeStops: localPrefs.coffeeStops === "morning" ? "both" : "afternoon" },
          stopTimeOverrides,
          "add_stop",
          ["coffeeStops"],
        ),
    });
  if (!hasApres)
    addStops.push({
      label: "+ Apres",
      apply: () => restop({ ...localPrefs, apres: "quiet" }, stopTimeOverrides, "add_stop", ["apres"]),
    });

  if (plan.itinerary.legs.length === 0) {
    return (
      <div className="rounded-[16px] border border-border-light bg-white p-5 text-[13px] text-text-muted">
        There is nothing to edit yet. Map a day first, then adjust the runs and stops.
      </div>
    );
  }

  return (
    <div className="rounded-[16px] border border-border-light bg-white p-4 md:p-5" data-export-ignore>
      <div className="mb-3 flex items-baseline justify-between">
        <h3 className="font-serif text-[16px] font-bold text-navy">Adjust your day</h3>
        {busy ? <span className="text-[12px] text-text-muted">Updating…</span> : null}
      </div>

      {error ? (
        <p className="mb-3 rounded-[10px] bg-amber-50 px-3 py-2 text-[12px] text-amber-800">{error}</p>
      ) : null}

      <ol className="space-y-2" aria-busy={busy}>
        {rows.map((row) =>
          row.type === "descent" ? (
            <li key={`d${row.legIndex}`} className="rounded-[12px] border border-border-light px-3 py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[13.5px] font-semibold leading-tight text-navy">
                    <span className="mr-1 tabular-nums text-text-muted">{hhmm(row.time)}</span>
                    {row.name}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-text-muted">
                    <span className="font-semibold text-gold">{row.score == null ? "Forecast unavailable" : `Lowest section ${scoreOutOf100Label(row.score)}`}</span>
                    {row.aspect ? <span>{ASPECT_LABEL[row.aspect] ?? row.aspect}</span> : null}
                  </div>
                </div>
                {findAlts(plan, row.legIndex).length > 0 ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setSwapOpen(swapOpen === row.legIndex ? null : row.legIndex)}
                    className="inline-flex min-h-[44px] shrink-0 items-center rounded-sm border border-border-light px-3 text-[12.5px] font-semibold text-navy hover:border-gold/40 disabled:opacity-50"
                    aria-expanded={swapOpen === row.legIndex}
                  >
                    Swap run
                  </button>
                ) : null}
              </div>

              {swapOpen === row.legIndex ? (
                <div className="mt-2 space-y-1 border-t border-border-light pt-2">
                  <p className="text-[11.5px] text-text-muted">Other runs from here:</p>
                  {findAlts(plan, row.legIndex).slice(0, 6).map((alt) => (
                    <button
                      key={alt.edgeId}
                      type="button"
                      disabled={busy}
                      onClick={() => swapRun(row.legIndex, alt.edgeId)}
                      className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-[8px] px-2 text-left hover:bg-warm-bg disabled:opacity-50"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span
                          aria-hidden
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: difficultyColour(alt.difficulty, sport) }}
                        />
                        <span className="truncate text-[13px] font-medium text-navy">
                          {alt.name ?? "Unnamed run"}
                        </span>
                        <span className="shrink-0 text-[11px] text-text-muted">
                          {difficultySymbol(alt.difficulty, sport) ? (
                            <span aria-hidden style={{ color: difficultyColour(alt.difficulty, sport) }}>
                              {difficultySymbol(alt.difficulty, sport)}{" "}
                            </span>
                          ) : null}
                          {difficultyLabel(alt.difficulty, sport)}
                        </span>
                      </span>
                      <span className="shrink-0 text-[12px] font-semibold text-gold">{alt.score == null ? "Forecast unavailable" : scoreOutOf100Label(alt.score)}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </li>
          ) : (
            <li key={`s${row.legIndex}`} className="rounded-[12px] border border-gold/30 bg-gold/[0.05] px-3 py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[13.5px] font-semibold leading-tight text-navy">
                    <span className="mr-1" aria-hidden>{STOP_ICON[row.stopType]}</span>
                    <span className="mr-1 tabular-nums text-text-muted">{hhmm(row.time)}</span>
                    {row.label}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => retimeStop(row, -15)}
                    aria-label={`Move ${row.stopType} 15 minutes earlier`}
                    className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-sm border border-border-light text-navy hover:border-gold/40 disabled:opacity-50"
                  >
                    −
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => retimeStop(row, 15)}
                    aria-label={`Move ${row.stopType} 15 minutes later`}
                    className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-sm border border-border-light text-navy hover:border-gold/40 disabled:opacity-50"
                  >
                    +
                  </button>
                  {venuesFor(row).length > 1 ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setVenueOpen(venueOpen === row.legIndex ? null : row.legIndex)}
                      className="inline-flex min-h-[44px] items-center rounded-sm border border-border-light px-2.5 text-[12px] font-semibold text-navy hover:border-gold/40 disabled:opacity-50"
                      aria-expanded={venueOpen === row.legIndex}
                    >
                      Change
                    </button>
                  ) : null}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => removeStop(row.stopType, row.time)}
                    aria-label={`Remove ${row.stopType} stop`}
                    className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-sm border border-border-light text-text-muted hover:border-red-300 hover:text-red-600 disabled:opacity-50"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {venueOpen === row.legIndex ? (
                <div className="mt-2 space-y-1 border-t border-gold/20 pt-2">
                  {venuesFor(row).map((v) => (
                    <button
                      key={v.placeId}
                      type="button"
                      disabled={busy}
                      onClick={() => changeVenue(row.legIndex, v)}
                      className={`flex min-h-[44px] w-full items-center justify-between gap-2 rounded-[8px] px-2 text-left hover:bg-white disabled:opacity-50 ${
                        v.placeId === row.placeId ? "bg-white" : ""
                      }`}
                    >
                      <span className="min-w-0 text-[13px] font-medium text-navy">{v.name || "A mountain spot"}{v.source === "openstreetmap" && <span className="mt-1 block text-xs font-normal text-text-muted">{placeEvidenceLabel(v)}</span>}</span>
                      <span className="flex shrink-0 items-center gap-1.5 text-[11.5px] text-text-muted">
                        {v.rating != null ? <span>★ {v.rating.toFixed(1)}</span> : null}
                        {priceTierLabel(v.priceLevel) ? <span>{priceTierLabel(v.priceLevel)}</span> : null}
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}
            </li>
          ),
        )}
      </ol>

      {addStops.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2 border-t border-border-light pt-3">
          {addStops.map((a) => (
            <button
              key={a.label}
              type="button"
              disabled={busy}
              onClick={a.apply}
              className="inline-flex min-h-[44px] items-center rounded-sm border border-border-light bg-warm-bg px-3 text-[12.5px] font-semibold text-navy hover:border-gold/40 disabled:opacity-50"
            >
              {a.label}
            </button>
          ))}
        </div>
      ) : null}

      {plan.routeAlternatives.length > 0 ? (
        <div className="mt-3 border-t border-border-light pt-3">
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.5px] text-text-muted">
            Try a different route
          </p>
          <div className="mt-2 space-y-1">
            {plan.routeAlternatives.slice(0, 3).map((alternative) => (
              <button
                key={alternative.id}
                type="button"
                disabled={busy}
                onClick={() => tryRouteAlternative(alternative)}
                className="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-[8px] px-2 text-left hover:bg-warm-bg disabled:opacity-50"
              >
                <span className="min-w-0">
                  <span className="block truncate text-[13px] font-semibold text-navy">
                    {alternative.label}
                  </span>
                  <span className="block truncate text-[11.5px] text-text-muted">
                    {routeAlternativeEditorMeta(alternative)}
                  </span>
                </span>
                <span className="shrink-0 text-[12px] font-semibold text-gold">Try</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function findAlts(plan: ItinerarySuccess, legIndex: number) {
  return plan.descentAlternatives.find((d) => d.legIndex === legIndex)?.options ?? [];
}

/** Group legs into the editor's editable rows: descents and stops only. */
function buildRows(plan: ItinerarySuccess): Row[] {
  const legs = plan.itinerary.legs;
  const rows: Row[] = [];
  let i = 0;
  while (i < legs.length) {
    const leg = legs[i];
    if (leg.kind === "stop") {
      rows.push({
        type: "stop",
        legIndex: i,
        stopType: leg.stopType,
        time: leg.departHour,
        label: leg.label,
        nodeId: leg.nodeId,
        placeId: leg.placeId,
      });
      i += 1;
      continue;
    }
    if (leg.kind === "trail" && !leg.climb) {
      const firstIndex = i;
      const departHour = leg.departHour;
      const names: string[] = [];
      let best: number | null = null;
      let missingScore = false;
      let aspect: string | null = null;
      while (i < legs.length && legs[i].kind === "trail") {
        const t = legs[i];
        if (t.kind === "trail" && t.climb) break;
        if (t.kind === "trail") {
          if (t.label && !names.includes(t.label)) names.push(t.label);
          if (t.trail?.score == null) missingScore = true;
          if (t.trail?.score != null && (best == null || t.trail.score < best)) {
            best = t.trail.score;
            aspect = t.trail?.aspect ?? null;
          }
        }
        i += 1;
      }
      const name =
        names.length === 0
          ? plan.preferences?.sport === "mtb" ? "Ride down" : "Ski down"
          : names.length <= 2
            ? names.join(", then ")
            : `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
      rows.push({ type: "descent", legIndex: firstIndex, time: departHour, name, score: missingScore ? null : best, aspect });
      continue;
    }
    i += 1; // lifts, links and buses are not editable
  }
  return rows;
}
